import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Contact } from "@/components/site/Contact";
import {
  SERVICES,
  PLANITY_URL,
  EPILATION_EXTRAS,
  REGARD_PREP,
  VISAGE_PREP,
} from "@/data/content";
import { EpilationExtras } from "@/components/site/EpilationExtras";
import { RegardServices } from "@/components/site/RegardServices";
import { VisageServices } from "@/components/site/VisageServices";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useSeo } from "@/hooks/useSeo";

const PrepMention = ({ text }) => (
  <div data-testid="avant-rdv-mention" className="mt-6">
    <p className="text-[11px] tracking-[0.22em] uppercase text-[#b07a63]">
      Avant votre rendez-vous
    </p>
    <p className="mt-1.5 text-sm text-[#944639]">{text}</p>
  </div>
);

export default function ServiceDetail() {
  const { slug } = useParams();
  const s = SERVICES.find((x) => x.slug === slug);

  useSeo({
    title: s ? `${s.name} à Toulouse — Ninaa Beauty` : "",
    description: s
      ? `${s.desc}. ${s.name} chez Ninaa Beauty, institut de beauté à Toulouse centre, près de Jean-Jaurès et Matabiau.`
      : "",
    path: s ? `/prestations/${s.slug}` : "",
  });

  if (!s) return <Navigate to="/" replace />;

  return (
    <div className="App" data-testid="service-detail-page">
      <Header />
      <main className="page-enter bg-white">
        <div className="max-w-6xl mx-auto px-6 py-14 md:py-20">
          <Link
            to="/#prestations"
            data-testid="back-to-prestations"
            className="inline-flex items-center gap-2 text-sm text-[#4b2018] hover:text-[#4b2018] transition-colors mb-10"
          >
            <ArrowLeft size={16} /> Toutes les prestations
          </Link>

          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            {/* grande image */}
            <div className="overflow-hidden rounded-md">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={s.image}
                  alt={`${s.name} à Toulouse — Ninaa Beauty`}
                  data-testid="detail-image"
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              </div>
            </div>

            {/* contenu minimaliste */}
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-[#b07a63] mb-5">
                Prestation
              </p>
              <h1
                data-testid="detail-name"
                className="font-serif-display font-light text-[#4b2018] text-4xl md:text-6xl leading-[1]"
              >
                {s.name}
              </h1>

              <p className="mt-8 text-[#4b2018] leading-relaxed max-w-md whitespace-pre-line">
                {s.description}
              </p>

              <a
                href={PLANITY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="detail-reserve-btn"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#4e231b] text-white px-7 py-3.5 text-sm hover:bg-[#b07a63] transition-colors duration-400"
              >
                Réserver <ArrowRight size={16} />
              </a>

              {slug === "epilations" && (
                <Accordion
                  type="single"
                  collapsible
                  className="mt-6 max-w-md"
                >
                  <AccordionItem
                    value="avant"
                    data-testid="avant-epilation-accordion"
                    className="border-b-0"
                  >
                    <AccordionTrigger className="rounded-md border border-[#e3c37a]/50 bg-[#e3c37a]/[0.15] px-4 py-3 text-[15px] font-medium tracking-wide text-[#4b2018] underline decoration-1 underline-offset-[7px] decoration-[#5a2b2b]/50 hover:text-[#4e231b]">
                      Avant votre épilation
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pt-4 space-y-4">
                        {EPILATION_EXTRAS.prep.slice(0, 3).map((item, i) => (
                          <div key={i}>
                            <p className="text-xs tracking-[0.15em] uppercase text-[#b07a63] mb-1">
                              {item.t}
                            </p>
                            <p className="text-sm leading-relaxed text-[#4b2018]">
                              {item.d}
                            </p>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              )}

              {slug === "beaute-du-regard" && <PrepMention text={REGARD_PREP} />}
              {slug === "soins-du-visage" && <PrepMention text={VISAGE_PREP} />}
            </div>
          </div>

          <div id="prestations-detail" className="scroll-mt-24">
            {slug === "epilations" && <EpilationExtras />}
            {slug === "beaute-du-regard" && <RegardServices />}
            {slug === "soins-du-visage" && <VisageServices />}
          </div>
        </div>
      </main>
      <Contact />
    </div>
  );
}
